import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { getConversationPrint, photoUrl } from "../lib/api";
import { SPECIALISTS, specialistId } from "../lib/specialists";

const List = ({ items }) => (items?.length ? <ul className="list-disc pl-5 space-y-0.5">{items.map((x, i) => <li key={i}>{typeof x === "string" ? x : `${x.title}: ${x.detail}`}</li>)}</ul> : null);

const Msg = ({ m, i }) => {
  const s = m.role === "agent" ? SPECIALISTS[specialistId(m.speaker)] : null;
  return (
    <div data-testid={`print-msg-${i}`} className="flex gap-3 py-2.5 border-b border-slate-200 break-inside-avoid">
      {s ? <img src={s.face} alt="" className="h-9 w-9 rounded-full object-cover shrink-0" /> : <div className="h-9 w-9 rounded-full bg-slate-200 shrink-0" />}
      <div className="min-w-0">
        <div className="text-xs font-bold text-[#14532D]">{s ? s.name : "You"}{s && <span className="font-normal text-slate-500"> · {s.title}</span>}</div>
        <div className="text-sm whitespace-pre-wrap">{m.text}</div>
      </div>
    </div>
  );
};

export default function PrintConversation() {
  const { sessionId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { getConversationPrint(sessionId).then(setD).catch(() => setErr(true)); }, [sessionId]);
  if (err) return <p data-testid="print-error" className="p-10 text-center text-slate-600">This conversation copy isn't available.</p>;
  if (!d) return <p className="p-10 text-center text-slate-500">Loading…</p>;
  const a = d.assessment || {};
  const b = d.business;
  return (
    <div data-testid="print-conversation" className="bg-white text-slate-900 mx-auto max-w-3xl px-6 py-8 print:p-0 print:max-w-none">
      <div className="print:hidden mb-5 flex justify-end">
        <button data-testid="print-now-button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full bg-[#14532D] text-white px-5 py-2.5 text-sm font-bold">
          <Printer size={16} /> Print
        </button>
      </div>
      <header className="text-center border-b-2 border-[#14532D] pb-3">
        <img data-testid="print-logo" src="/brand/phdg-logo.png" alt={b.group} className="mx-auto h-auto w-56" />
        <div className="text-xs text-slate-600">{b.division} · {b.city}</div>
      </header>
      <div data-testid="print-label" className="mt-4 text-center text-xs font-bold tracking-widest border border-slate-400 py-1.5">CONVERSATION COPY – NOT THE PERSONALIZED DIAGNOSTIC BRIEF</div>
      <p className="mt-3 text-sm">Case {d.case_id} · {d.case_date}{d.first_name ? ` · ${d.first_name}` : ""}{d.location ? ` · ${d.location}` : ""}</p>
      {d.concern && <p className="text-sm"><strong>Your concern:</strong> {d.concern}</p>}
      {(d.photos.length > 0 || a.summary) && <>
      <h2 className="mt-5 font-bold text-[#14532D]">Original Photo Assessment</h2>
      <div className="mt-2 flex flex-wrap gap-2">{d.photos.map((p) => <img key={p.id} src={photoUrl(p.id)} alt={p.label} className="h-32 w-auto rounded border" />)}</div>
      <div className="mt-2 text-sm space-y-2">
        {a.summary && <p>{a.summary}</p>}
        {a.observations?.length > 0 && <h3 className="font-bold">Observations</h3>}
        <List items={a.observations} />
        {(a.areas_of_concern || []).map((g, i) => <div key={i}><strong>{g?.category}</strong><List items={g?.items} /></div>)}
        {a.next_steps?.length > 0 && <h3 className="font-bold">Recommended Next Steps</h3>}
        <List items={a.next_steps} />
      </div>
      </>}
      <h2 className="mt-6 font-bold text-[#14532D]">Ask Dr. Verdant Conversation</h2>
      {d.messages.length ? d.messages.map((m, i) => <Msg key={i} m={m} i={i} />) : <p className="text-sm text-slate-500">No conversation saved yet.</p>}
      <p data-testid="print-disclaimer" className="mt-5 text-xs text-slate-600 border border-slate-300 p-2.5">{d.disclaimer}</p>
      <footer className="mt-6 pt-3 border-t text-center text-[11px] text-slate-600">
        {b.group} · {b.division} · {b.city}<br />Office: {b.office} · PH&amp;DG: {b.phdg} · {b.email}<br /><em>{b.tagline}</em>
      </footer>
    </div>
  );
}
